import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  Input,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@travellegacy/nexops-design-system';
import { Loader2, Save, ShieldCheck, Trash2, X } from 'lucide-react';
import type { SLAConfig } from '../api/demands.api';
import {
  useDeleteSLA,
  useDemandsFacets,
  useSLAs,
  useUpsertSLA,
} from '../hooks/useDemands';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface DraftSLA {
  type: string;
  targetHours: string;
  warningHours: string;
  enabled: boolean;
  existingId?: string;
  dirty: boolean;
}

/**
 * Modal para gerenciar SLAs por tipo de demanda.
 * Lista todos os tipos conhecidos (via facets) + tipos com SLA já configurado
 * mesmo que não apareçam mais no facets. Cada linha permite editar meta e
 * warning em horas, habilitar/desabilitar e remover.
 */
export function SLAConfigModal({ open, onOpenChange }: Props) {
  const slasQuery = useSLAs();
  const facetsQuery = useDemandsFacets();
  const upsert = useUpsertSLA();
  const remove = useDeleteSLA();

  const [drafts, setDrafts] = useState<Record<string, DraftSLA>>({});
  const [savingType, setSavingType] = useState<string | null>(null);
  const [deletingType, setDeletingType] = useState<string | null>(null);

  const slas = slasQuery.data?.data ?? [];
  const facetTypes = facetsQuery.data?.data.types ?? [];

  const rows = useMemo(() => {
    const byType = new Map<string, SLAConfig>();
    for (const s of slas) byType.set(s.type, s);
    // União: tipos com SLA + tipos vistos nas demandas
    const allTypes = Array.from(
      new Set([...byType.keys(), ...facetTypes.filter((t): t is string => !!t)])
    ).sort((a, b) => a.localeCompare(b));

    return allTypes.map((type) => {
      const existing = byType.get(type);
      const draft = drafts[type];
      return {
        type,
        existing,
        draft:
          draft ??
          ({
            type,
            targetHours: existing ? msToHours(existing.targetMs) : '',
            warningHours:
              existing && existing.warningMs !== null
                ? msToHours(existing.warningMs)
                : '',
            enabled: existing?.enabled ?? true,
            existingId: existing?.id,
            dirty: false,
          } as DraftSLA),
      };
    });
  }, [slas, facetTypes, drafts]);

  function updateDraft(type: string, patch: Partial<DraftSLA>) {
    setDrafts((prev) => {
      const row = rows.find((r) => r.type === type);
      const base = prev[type] ?? row?.draft;
      if (!base) return prev;
      return { ...prev, [type]: { ...base, ...patch, dirty: true } };
    });
  }

  async function handleSave(type: string) {
    const draft = drafts[type] ?? rows.find((r) => r.type === type)?.draft;
    if (!draft) return;
    const targetHours = Number(draft.targetHours);
    if (!Number.isFinite(targetHours) || targetHours <= 0) return;
    const warningHours =
      draft.warningHours && Number(draft.warningHours) > 0
        ? Number(draft.warningHours)
        : undefined;

    setSavingType(type);
    try {
      await upsert.mutateAsync({
        type,
        targetHours,
        warningHours,
        enabled: draft.enabled,
      });
      setDrafts((prev) => {
        const copy = { ...prev };
        delete copy[type];
        return copy;
      });
    } finally {
      setSavingType(null);
    }
  }

  async function handleDelete(id: string, type: string) {
    setDeletingType(type);
    try {
      await remove.mutateAsync(id);
      setDrafts((prev) => {
        const copy = { ...prev };
        delete copy[type];
        return copy;
      });
    } finally {
      setDeletingType(null);
    }
  }

  const isLoading = slasQuery.isLoading || facetsQuery.isLoading;

  const configuredCount = slas.filter((s) => s.enabled).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl flex flex-col p-0 overflow-hidden">
        {/* Header customizado — grid de duas colunas para alinhar título e X
            perfeitamente no mesmo baseline. */}
        <header className="grid grid-cols-[1fr_auto] items-start gap-4 px-6 py-5 border-b border-border">
          <div className="min-w-0">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <h2 className="text-lg font-semibold leading-tight text-card-foreground">
                Configuração de SLA por tipo
              </h2>
            </div>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              Defina uma meta (em horas) para cada tipo de demanda. Ciclos que
              passarem da meta são marcados como{' '}
              <span className="font-medium text-foreground">Fora do SLA</span>.
              O warning (opcional) é o limiar amarelo antes de estourar a meta.
            </p>
          </div>

          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {/* Corpo com scroll */}
        <div className="flex-1 overflow-y-auto px-6 py-5 max-h-[60vh]">
          {isLoading ? (
            <p className="text-sm text-muted-foreground py-12 text-center">
              Carregando tipos e SLAs…
            </p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-12 text-center">
              Nenhum tipo de demanda encontrado. Assim que demandas com tipo
              forem criadas no Notion, elas aparecerão aqui.
            </p>
          ) : (
            <div className="rounded-lg border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="w-32">Meta (h)</TableHead>
                    <TableHead className="w-32">Warning (h)</TableHead>
                    <TableHead className="w-24 text-center">Ativo</TableHead>
                    <TableHead className="w-40 text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(({ type, existing, draft }) => {
                    const isSaving = savingType === type;
                    const isDeleting = deletingType === type;
                    const canSave =
                      draft.dirty &&
                      Number(draft.targetHours) > 0 &&
                      (draft.warningHours === '' ||
                        Number(draft.warningHours) > 0);
                    return (
                      <TableRow key={type}>
                        <TableCell className="align-middle">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <Badge variant="info">{type}</Badge>
                            {existing && !existing.enabled && (
                              <Badge variant="outline">desativado</Badge>
                            )}
                            {draft.dirty && (
                              <Badge variant="warning">não salvo</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="align-middle">
                          <Input
                            type="number"
                            min={0.1}
                            step={0.5}
                            value={draft.targetHours}
                            onChange={(e) =>
                              updateDraft(type, { targetHours: e.target.value })
                            }
                            placeholder="ex: 24"
                          />
                        </TableCell>
                        <TableCell className="align-middle">
                          <Input
                            type="number"
                            min={0.1}
                            step={0.5}
                            value={draft.warningHours}
                            onChange={(e) =>
                              updateDraft(type, {
                                warningHours: e.target.value,
                              })
                            }
                            placeholder="opcional"
                          />
                        </TableCell>
                        <TableCell className="align-middle">
                          <div className="flex justify-center">
                            <Switch
                              checked={draft.enabled}
                              onCheckedChange={(v) =>
                                updateDraft(type, { enabled: v })
                              }
                            />
                          </div>
                        </TableCell>
                        <TableCell className="text-right align-middle">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="default"
                              disabled={!canSave || isSaving}
                              onClick={() => handleSave(type)}
                            >
                              {isSaving ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Save className="h-3.5 w-3.5" />
                              )}
                              <span className="ml-1.5">Salvar</span>
                            </Button>
                            {existing && (
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={isDeleting}
                                onClick={() => handleDelete(existing.id, type)}
                                aria-label={`Remover SLA de ${type}`}
                                title="Remover SLA"
                              >
                                {isDeleting ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Trash2 className="h-3.5 w-3.5" />
                                )}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Footer com resumo à esquerda e ação à direita */}
        <footer className="flex items-center justify-between gap-4 px-6 py-4 border-t border-border bg-muted/20">
          <p className="text-xs text-muted-foreground tabular-nums">
            {rows.length} tipo{rows.length === 1 ? '' : 's'} ·{' '}
            <span className="font-medium text-foreground">
              {configuredCount}
            </span>{' '}
            SLA{configuredCount === 1 ? '' : 's'} ativo
            {configuredCount === 1 ? '' : 's'}
          </p>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </footer>
      </DialogContent>
    </Dialog>
  );
}

function msToHours(ms: bigint | number): string {
  const n = typeof ms === 'bigint' ? Number(ms) : ms;
  const hours = n / 3_600_000;
  // Se for inteiro, mostrar sem decimal; senão 1 casa.
  return Number.isInteger(hours) ? hours.toString() : hours.toFixed(1);
}
